/** IXD vs Workbook 구조 비교 초기 업로드 뷰 */

import { FileUploadSection } from "@/components/file-upload-section";
import { IxdUploadNotice } from "@/components/ixd-upload-notice";
import { NewChip } from "@/components/common/new-chip";
import { WorkbookPicker } from "@/components/ixd-workbook-compare/workbook-picker";
import { Button } from "@/styles/ui/button";
import { ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface InitialUploadViewProps {
  ixdFile: File | null;
  onIxdSelect: (file: File) => void;
  selectedWorkbookId: string | null;
  onWorkbookSelect: (workbookId: string | null) => void;
  onCompare: () => void;
  canCompare: boolean;
  isComparing: boolean;
  errorMessage?: string | null;
}

export const InitialUploadView = ({
  ixdFile,
  onIxdSelect,
  selectedWorkbookId,
  onWorkbookSelect,
  onCompare,
  canCompare,
  isComparing,
  errorMessage,
}: InitialUploadViewProps) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-neutral-100">
      <header className="flex h-14 px-6 items-center gap-4 border-b border-neutral-200 bg-white/80 backdrop-blur-sm">
        <button
          type="button"
          aria-label="홈으로 돌아가기"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-neutral-950">
            IXD / Workbook 구조 비교
          </h1>
          <NewChip color="indigo" size="large" />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-neutral-900 mb-3">
              IXD 파일과 workbook 태깅을 비교해보세요
            </h2>
            <p className="text-neutral-600 max-w-lg mx-auto">
              IXD 파일을 업로드하고 workbook을 선택하면 위계·태그·별칭·합계열·
              축 순서·행/열 전환·데이터타입의 차이를 보여줍니다.
            </p>
          </div>

          <div className="flex gap-6 mb-8">
            <div className="flex-1 flex flex-col gap-3">
              <FileUploadSection
                label="IXD 파일"
                accept=".ixd"
                onFileSelect={onIxdSelect}
                isLoading={false}
                fileName={ixdFile?.name}
                description=".ixd 형식"
                accentColor="blue"
              />
            </div>
            <WorkbookPicker
              selectedWorkbookId={selectedWorkbookId}
              onSelect={onWorkbookSelect}
            />
          </div>

          <IxdUploadNotice className="mb-8" />

          <div className="flex flex-col items-center gap-3">
            <Button
              onClick={onCompare}
              disabled={!canCompare || isComparing}
              size="lg"
              className="px-12 h-12 text-base bg-neutral-900 hover:bg-neutral-800 shadow-lg shadow-neutral-200"
            >
              {isComparing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  비교 분석 중...
                </>
              ) : (
                <>
                  비교 시작하기
                  <ChevronRight className="w-5 h-5 ml-1" />
                </>
              )}
            </Button>
            {errorMessage && (
              <p className="text-sm text-rose-600">{errorMessage}</p>
            )}
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-neutral-500">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span>IXD에만 있음</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-rose-500" />
              <span>Workbook에만 있음</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span>변경됨</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
